import type { AmlBucket, AmlMatch, AmlScreeningResult, AmlSeverity } from "./types";

type Raw = Record<string, unknown>;

const text = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.replace(/\s+/g, " ").trim();
  return trimmed.length ? trimmed : null;
};

const list = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((item) => text(item)).filter((item): item is string => Boolean(item))
    : [];

const number = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/** Apollo-style padded JSON blob inside additional_info.details. */
const parseDetails = (value: unknown) => {
  const raw = text(value);
  if (!raw) return {} as Raw;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Raw) : { details: raw };
  } catch {
    return { details: raw } as Raw;
  }
};

const isPep = (sourceCategory: string | null, subCategories: string[], categories: string[]) => {
  const haystack = [sourceCategory, ...subCategories, ...categories].join(" ").toLowerCase();
  return haystack.includes("politically") || /\bpep\b/.test(haystack);
};

const bucketOf = (
  country: string | null,
  sourceCategory: string | null,
  subCategories: string[],
  categories: string[]
): AmlBucket => {
  if (isPep(sourceCategory, subCategories, categories)) return "pep";
  if (!country || country.toLowerCase() === "india") return "amlPenalties";
  return "sanctions";
};

const severityOf = (matchType: string | null, score: number | null): AmlSeverity => {
  if (matchType?.toUpperCase() === "EXACT") return "high";
  if ((score ?? 0) >= 75) return "medium";
  return "low";
};

const normalizeMatch = (raw: Raw, index: number): AmlMatch => {
  const info = (
    raw["additional_info"] && typeof raw["additional_info"] === "object"
      ? raw["additional_info"]
      : {}
  ) as Raw;
  const details = parseDetails(info["details"]);
  const matchType = text(raw["match_type"]);
  const score = number(raw["score"]);
  const categories = list(raw["category_filter"]);
  const subCategories = list(raw["sub_category_filter"]);
  const sourceCategory = text(raw["source_category"]);

  return {
    caseId: text(raw["uniq_case_id"]) ?? `match-${index}`,
    source: text(raw["source"]) ?? "Unknown source",
    sourceType: text(raw["source_type"]),
    subSourceType: text(raw["sub_source_type"]),
    sourceCountry: text(raw["source_country"]),
    sourceContinent: text(raw["source_continent"]),
    sourceCategory,
    categories,
    subCategories,
    name: text(raw["name"]) ?? "Name not reported",
    alias: text(raw["alias"]),
    fatherName: text(raw["father_name"]),
    address: text(raw["address"]),
    gender: text(raw["gender"]),
    dob: text(raw["dob"]),
    birthYear: text(raw["birth_year"]),
    placeOfBirth: text(raw["place_of_birth"]),
    nationality: text(raw["nationality"]),
    crimeType: text(raw["crime_type"]),
    panNumber: text(raw["pan_number"]),
    phone: text(raw["phone_no"]),
    email: text(raw["email"]),
    photo: text(raw["photo"]),
    score,
    matchType,
    nameMatchType: text(raw["name_match_type"]),
    dobMatchType: text(raw["dob_match_type"]),
    fatherMatchType: text(raw["father_match_type"]),
    addressMatchType: text(raw["address_match_type"]),
    severity: severityOf(matchType, score),
    bucket: bucketOf(text(raw["source_country"]), sourceCategory, subCategories, categories),
    amount: text(info["amount"]),
    outstandingAmount: text(info["outstanding_amount"]),
    companyName: text(info["company_name"]),
    borrowerName: text(info["borrower_name"]),
    bankName: text(info["bank_name"]),
    bankBranch: text(info["bank_branch"]),
    otherBank: text(info["other_bank"]),
    orderDate: text(info["order_date"]),
    year: text(info["year"]),
    directorsInfo: text(info["directors_info"]),
    directorsDinInfo: text(info["directors_din_info"]),
    summary: text(details["details"]),
    description: text(details["description"]),
    pressReleaseDate: text(details["press_release_date"]),
    evidenceLink: text(raw["evidence_link"]),
    evidenceUrl: text(raw["evidence_url"]),
    pdfUrl: text(info["pdf_url"]),
    sourceUrl: text(info["source_url"]),
    caseDetailsLink: text(raw["case_details_link"]),
  };
};

/** Unwraps `{ data: amlJson }` or returns the payload as-is. */
const unwrapPayload = (payload: unknown): Raw => {
  if (!payload || typeof payload !== "object") return {};
  const root = payload as Raw;
  if (root["data"] && typeof root["data"] === "object" && !Array.isArray(root["data"])) {
    return root["data"] as Raw;
  }
  return root;
};

/** Turns a raw screening response into the shape the UI renders. */
export function normalizeScreening(payload: unknown, screenedName: string): AmlScreeningResult {
  const raw = unwrapPayload(payload);
  const results = Array.isArray(raw["results"]) ? (raw["results"] as Raw[]) : [];
  const matches = results.map(normalizeMatch);

  const counts = new Map<string, number>();
  for (const match of matches) counts.set(match.source, (counts.get(match.source) ?? 0) + 1);

  return {
    status: text(raw["status"]) ?? "COMPLETED",
    total: number(raw["total"]) ?? matches.length,
    searchId: matches[0]?.caseId ? text(results[0]?.["search_id"]) : null,
    screenedName,
    screenedAt: new Date().toISOString(),
    matches,
    exactCount: matches.filter((match) => match.matchType?.toUpperCase() === "EXACT").length,
    fuzzyCount: matches.filter((match) => match.matchType?.toUpperCase() !== "EXACT").length,
    sources: [...counts.entries()]
      .map(([source, count]) => ({ source, count }))
      .sort((a, b) => b.count - a.count),
  };
}

export const bucketMatches = (
  result: AmlScreeningResult | null,
  bucket: AmlBucket
) => (result ? result.matches.filter((match) => match.bucket === bucket) : []);
